import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserEventNotificationPreferences } from "@/interfaces";
import { errorHandler } from "@/lib/utils";
import { UpdateUserEventNotificationPreferenceInput } from "@/services/api/userEventNotificationPreferences";
import { DefaultResponse } from "@/services/api/types";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { CircleDot, Mail, Save, Smartphone } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const PreferenceTableSkeleton = ({ rowCount = 5 }: { rowCount?: number }) => {
  return (
    <>
      {Array(rowCount)
        .fill(0)
        .map((_, index) => (
          <TableRow key={`skeleton-row-${index}`}>
            <TableCell className="truncate">
              <Skeleton className="h-6 w-full bg-muted" />
            </TableCell>
            <TableCell className="text-center">
              <div className="flex justify-center">
                <Skeleton className="h-5 w-10 rounded-full bg-muted" />
              </div>
            </TableCell>
            <TableCell className="text-center">
              <div className="flex justify-center">
                <Skeleton className="h-5 w-10 rounded-full bg-muted" />
              </div>
            </TableCell>
          </TableRow>
        ))}
    </>
  );
};

const StyledSwitch = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Switch
    checked={checked}
    onCheckedChange={onChange}
    className="data-[state=checked]:bg-green-500 data-[state=checked]:hover:bg-green-600"
  />
);

interface NotificationPreferencesTableProps {
  queryKey: QueryKey;
  fetchPreferences: () => Promise<
    DefaultResponse<UserEventNotificationPreferences>
  >;
  updatePreferences: (
    data: UpdateUserEventNotificationPreferenceInput[],
  ) => Promise<unknown>;
}

export const NotificationPreferencesTable = ({
  queryKey,
  fetchPreferences,
  updatePreferences,
}: NotificationPreferencesTableProps) => {
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState<
    UserEventNotificationPreferences[]
  >([]);
  const [originalPreferences, setOriginalPreferences] = useState<
    UserEventNotificationPreferences[]
  >([]);
  const [allChecked, setAllChecked] = useState(false);
  const [allMailChecked, setAllMailChecked] = useState(false);
  const [allPushChecked, setAllPushChecked] = useState(false);

  const isPreferencesDirty = useMemo(
    () =>
      preferences.some((pref) => {
        const original = originalPreferences.find(
          (p) => p.event_type_id === pref.event_type_id,
        );
        return (
          !!pref.channel_mail !== !!original?.channel_mail ||
          !!pref.channel_push !== !!original?.channel_push
        );
      }),
    [preferences, originalPreferences],
  );

  const updateAllCheckStates = useCallback(
    (prefs: UserEventNotificationPreferences[]) => {
      const allMail = prefs.every((p) => p.channel_mail);
      const allPush = prefs.every((p) => p.channel_push);
      setAllMailChecked(allMail);
      setAllPushChecked(allPush);
      setAllChecked(allMail && allPush);
    },
    [],
  );

  const {
    data,
    error,
    isPending: loading,
  } = useQuery({
    queryKey,
    queryFn: () => fetchPreferences(),
  });

  useEffect(() => {
    if (!data) return;

    setPreferences(data.data);
    setOriginalPreferences(data.data);
    updateAllCheckStates(data.data);
  }, [data, updateAllCheckStates]);

  useEffect(() => {
    if (!error) return;

    errorHandler(error, "Erro ao buscar preferências de notificação");
  }, [error]);

  const handleAllChange = (checked: boolean) => {
    setAllChecked(checked);
    setAllMailChecked(checked);
    setAllPushChecked(checked);

    setPreferences((prev) =>
      prev.map((pref) => ({
        ...pref,
        channel_mail: checked,
        channel_push: checked,
      })),
    );
  };

  const handleAllMailChange = (checked: boolean) => {
    setAllMailChecked(checked);
    setPreferences((prev) =>
      prev.map((pref) => ({
        ...pref,
        channel_mail: checked,
      })),
    );
    updateMasterCheckbox();
  };

  const handleAllPushChange = (checked: boolean) => {
    setAllPushChecked(checked);
    setPreferences((prev) =>
      prev.map((pref) => ({
        ...pref,
        channel_push: checked,
      })),
    );
    updateMasterCheckbox();
  };

  const updateMasterCheckbox = () => {
    const allChecks = preferences.every(
      (p) => p.channel_mail && p.channel_push,
    );
    setAllChecked(allChecks);
  };

  const handlePreferenceChange = (
    id: number,
    channel: "mail" | "push",
    checked: boolean,
  ) => {
    setPreferences((prev) =>
      prev.map((pref) => {
        if (pref.event_type_id === id) {
          return {
            ...pref,
            [channel === "mail" ? "channel_mail" : "channel_push"]: checked,
          };
        }
        return pref;
      }),
    );

    const updatedPrefs = preferences.map((pref) =>
      pref.event_type_id === id
        ? {
            ...pref,
            [channel === "mail" ? "channel_mail" : "channel_push"]: checked,
          }
        : pref,
    );
    updateAllCheckStates(updatedPrefs);
  };

  const { mutate: savePreferences, isPending: isSubmiting } = useMutation({
    mutationFn: updatePreferences,
    onSuccess: (response) => {
      const contextMessage =
        "Preferências de notificação atualizadas com sucesso!";

      const responseMessage =
        (response as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || contextMessage;

      setOriginalPreferences(preferences);
      toast.success(responseMessage);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      errorHandler(err, "Erro ao salvar preferências de notificação");
    },
  });

  const handleSave = () => {
    savePreferences(
      preferences.map((pref) => ({
        event_type_id: pref.event_type_id,
        channel_mail: pref.channel_mail ?? false,
        channel_push: pref.channel_push ?? false,
      })),
    );
  };

  const handlePreferencesCancel = () => {
    setPreferences(originalPreferences);
    updateAllCheckStates(originalPreferences);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Notificações
          {isPreferencesDirty && (
            <span className="flex items-center gap-1 text-xs font-normal text-amber-600 dark:text-amber-400">
              <CircleDot className="h-3 w-3" />
              Alterações não salvas
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="space-y-6 px-6">
          <div className="flex items-center gap-2">
            <span className="font-medium">Permitir tudo</span>
            {loading ? (
              <Skeleton className="h-5 w-10 rounded-full" />
            ) : (
              <StyledSwitch checked={allChecked} onChange={handleAllChange} />
            )}
          </div>

          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2 md:w-3/5 lg:w-2/3">
                  Evento
                </TableHead>
                <TableHead className="w-1/4 md:w-1/5 lg:w-1/6 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span className="hidden sm:inline">E-mail</span>
                    <StyledSwitch
                      checked={allMailChecked}
                      onChange={handleAllMailChange}
                    />
                  </div>
                </TableHead>
                <TableHead className="w-1/4 md:w-1/5 lg:w-1/6 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    <span className="hidden sm:inline">Mobile</span>
                    <StyledSwitch
                      checked={allPushChecked}
                      onChange={handleAllPushChange}
                    />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <PreferenceTableSkeleton rowCount={6} />
              ) : (
                preferences.map((pref) => (
                  <TableRow key={pref.event_type_id}>
                    <TableCell className="truncate">
                      {pref.description}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">
                        <StyledSwitch
                          checked={pref.channel_mail ?? false}
                          onChange={(checked) =>
                            handlePreferenceChange(
                              pref.event_type_id,
                              "mail",
                              checked,
                            )
                          }
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">
                        <StyledSwitch
                          checked={pref.channel_push ?? false}
                          onChange={(checked) =>
                            handlePreferenceChange(
                              pref.event_type_id,
                              "push",
                              checked,
                            )
                          }
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex gap-4 px-4 py-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSubmiting || loading || !isPreferencesDirty}
              className="flex items-center gap-2 bg-primary-500 dark:bg-primary-400 text-white dark:hover:opacity-90"
            >
              {isSubmiting ? (
                <Skeleton className="h-4 w-4 rounded-full" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSubmiting ? "Salvando..." : "Salvar"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handlePreferencesCancel}
              disabled={isSubmiting || loading || !isPreferencesDirty}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
